import { useMemo, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Table, Button, TableColumn, TableHeaderCell, DropdownItem } from 'common/components';
import { FileText, SquarePlay } from 'lucide-react';
import { formatCallHistory, FormatedCallHistoryItem } from 'pages/AgentCommandCenter/AgentCommandCenter.utils';
import { CallInfo } from 'types/agentCommandCenter.type';
import { CallHistoryItem } from 'services/api/AgentCenterAPI';
import { DATE_RANGE_OPTIONS } from 'pages/AgentCommandCenter/AgentCommandCenter.utils';

import styles from './CallHistory.module.scss';

type CallHistoryProps = {
    callHistory?: CallHistoryItem[];
    onViewTranscript: (callId: string, callInfo: CallInfo) => void;
    onListenRecording: (callId: string, callInfo: CallInfo) => void;
    isLoading: boolean;
};

export const CallHistory = ({ callHistory, onViewTranscript, onListenRecording, isLoading }: CallHistoryProps) => {
    const [startDate, setStartDate] = useState<Date | null>(new Date());
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [dateRangeType, setDateRangeType] = useState<string>('today');
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);

    const columns: TableColumn<FormatedCallHistoryItem>[] = [
        {
            key: 'callDate',
            dataIndex: 'callDate',
            title: (
                <TableHeaderCell
                    label='Date'
                    filterActive={dateRangeType !== 'today'}
                    filterDropdownClassName='flex'
                    filterContent={({ onClose }) => (
                        <>
                            <div>
                                {DATE_RANGE_OPTIONS.map((option) => (
                                    <DropdownItem
                                        key={option.value}
                                        label={option.label}
                                        value={option.value}
                                        isSelected={dateRangeType === option.value}
                                        onClick={(value) => {
                                            setDateRangeType(value as string);
                                            if (value !== 'custom') {
                                                onClose();
                                            }
                                        }}
                                    />
                                ))}
                            </div>
                            {dateRangeType === 'custom' && (
                                <div className={styles.dateInputGroup}>
                                    <ReactDatePicker
                                        selectsRange
                                        selected={startDate}
                                        onChange={(dates: [Date | null, Date | null]) => {
                                            const [start, end] = dates;
                                            setStartDate(start);
                                            setEndDate(end ?? null);
                                            if (start && end) {
                                                setDateRange(dates);
                                                onClose();
                                            }
                                        }}
                                        startDate={startDate}
                                        endDate={endDate}
                                        maxDate={new Date()}
                                        dateFormat='MM/dd/yy'
                                        inline
                                    />
                                </div>
                            )}
                        </>
                    )}
                />
            ),
        },
        {
            key: 'contactName',
            title: 'Contact Name',
            dataIndex: 'contactName',
        },
        {
            key: 'phoneNumber',
            title: 'Phone Number',
            dataIndex: 'phoneNumber',
        },
        {
            key: 'timeOfCall',
            title: 'Time of Call',
            dataIndex: 'timeOfCall',
        },
        {
            key: 'duration',
            title: 'Duration',
            dataIndex: 'duration',
        },
        {
            key: 'transcript',
            title: 'Transcript',
            align: 'center',
            render: (_, record) => (
                <Button
                    variant='ghost'
                    size='sm'
                    iconOnly
                    isRounded
                    icon={FileText}
                    onClick={() => onViewTranscript && onViewTranscript(record.callId, record)}
                    title='View transcript'
                />
            ),
        },
        {
            key: 'recording',
            title: 'Recording',
            align: 'center',
            render: (_, record) => (
                <Button
                    variant='ghost'
                    size='sm'
                    iconOnly
                    isRounded
                    icon={SquarePlay}
                    onClick={() => onListenRecording && onListenRecording(record.callId, record)}
                    title='Listen to recording'
                />
            ),
        },
        {
            key: 'actionTaken',
            title: 'Action Taken',
            dataIndex: 'actionTaken',
        },
    ];

    const calls = useMemo(() => {
        return formatCallHistory({ callHistory, dateFilter: dateRangeType, dateRange });
    }, [callHistory, dateRangeType, dateRange]);

    return (
        <div className={styles.callHistorySection}>
            <h2>Call History</h2>
            <Table
                data={calls}
                columns={columns}
                emptyText='No call history available'
                rowKey='callId'
                size='medium'
                variant='secondary'
                isLoading={isLoading}
            />
        </div>
    );
};
